import type { jsx } from '@emotion/react';

import type { ReviewAndSatisfactionScoreProps } from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationReviewsAndSatisfactionScoreItem';
import { MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS } from 'bundles/unified-description-page-common/constants';
import { KeyInformationItemNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import combineCourseDurationAndSchecule from 'bundles/unified-description-page-common/utils/combineCourseDurationAndSchecule';
import combineDurationAndSchedule from 'bundles/unified-description-page-common/utils/combineDurationAndSchedule';
import { combineReviewsAndSatisfactionScore } from 'bundles/unified-description-page-common/utils/combineReviewsAndSatisfactionScore';

type Props = {
  items: (jsx.JSX.Element | null)[];
  reviewItemIndex?: number;
  totalDuration?: string | null;
  totalLearningMonths?: number | null;
  learningHoursPerWeek?: number | null;
} & ReviewAndSatisfactionScoreProps;

export const getKeyInformationItemsToRender = ({
  items,
  totalDuration,
  totalLearningMonths,
  learningHoursPerWeek,
  productRating,
  totalReviewCount,
  enrollmentCount,
  satisfactionScore,
  satisfactionScoreSubtitle,
}: Props) => {
  let shouldRenderStackabilityInDetailsSection = false;
  let itemsToRender = items.filter((item) => item !== null);

  const getEarnCertificateIndex = () =>
    itemsToRender.findIndex((item) => item?.key === KeyInformationItemNames.EarnCertificate);
  const getScheduleItemIndex = () => itemsToRender.findIndex((item) => item?.key === KeyInformationItemNames.Schedule);
  const getDurationItemIndex = () => itemsToRender.findIndex((item) => item?.key === KeyInformationItemNames.Duration);
  const getStackabilityItemIndex = () =>
    itemsToRender.findIndex((item) => item?.key === KeyInformationItemNames.Stackability);
  const getReviewsItemIndex = () => itemsToRender.findIndex((item) => item?.key === KeyInformationItemNames.Reviews);
  const getSatisfactionScoreItemIndex = () =>
    itemsToRender.findIndex((item) => item?.key === KeyInformationItemNames.CourseSatisfactionScore);

  if (getEarnCertificateIndex() !== -1 && itemsToRender.length > MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS) {
    // remove stackability item and render in details to know section
    if (itemsToRender.length > MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS && getStackabilityItemIndex() !== -1) {
      itemsToRender.splice(getStackabilityItemIndex(), 1);
      shouldRenderStackabilityInDetailsSection = true;
    }

    // combine schedule and duration items, if both present
    if (
      itemsToRender.length > MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS &&
      getDurationItemIndex() !== -1 &&
      getScheduleItemIndex() !== -1
    ) {
      let combinedItems;
      // use s12n combination fn if either value is present
      // else use the course combination fn instead
      if (totalLearningMonths || learningHoursPerWeek) {
        combinedItems = combineDurationAndSchedule({
          items: itemsToRender,
          totalLearningMonths,
          learningHoursPerWeek,
        });
        itemsToRender = combinedItems.filter((item) => item !== null);
      } else {
        combinedItems = combineCourseDurationAndSchecule({
          items: itemsToRender,
          totalDuration,
        });
        itemsToRender = combinedItems.filter((item) => item !== null);
      }
    }

    // combine reviews and satisfaction score items, if both present
    if (
      itemsToRender.length > MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS &&
      getReviewsItemIndex() !== -1 &&
      getSatisfactionScoreItemIndex() !== -1
    ) {
      itemsToRender = combineReviewsAndSatisfactionScore({
        items: itemsToRender,
        productRating,
        totalReviewCount,
        enrollmentCount,
        satisfactionScore,
        satisfactionScoreSubtitle,
      });
    }

    // replace last item with earn certificate, where applicable
    if (itemsToRender.length > MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS) {
      const earnCertificate = itemsToRender.find((item) => item?.key === KeyInformationItemNames.EarnCertificate);
      const earnCertificateIndex = getEarnCertificateIndex();

      itemsToRender = itemsToRender.slice(0, MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS);
      if (earnCertificate && earnCertificateIndex >= MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS) {
        itemsToRender.splice(MAX_NUMBER_OF_HORIZONTAL_KIM_ITEMS - 1, 1, earnCertificate);
      }
    }
  }

  return {
    itemsToRender,
    shouldRenderStackabilityInDetailsSection,
    keyInformationIncludesReview: getReviewsItemIndex() !== -1,
  };
};
